/**
 * Localizations Panel Component
 *
 * Manages word list name/description localizations.
 * Works for both create and edit - stores changes locally,
 * parent saves on form submit.
 */

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { useLocalizations, useGenerateDraftLocalizations } from '../services/localizationApi';
import type { WordListLocalizationCreate } from '../types/localization.types';

interface LocalizationsPanelProps {
  listId?: string; // Optional - if provided, loads existing from DB
  listName: string;
  listDescription?: string | null;
  listLanguageCode: string;
  localizations: WordListLocalizationCreate[];
  onChange: (localizations: WordListLocalizationCreate[]) => void;
}

type PanelMode = 'view' | 'add' | 'edit';

interface EditingLocalization {
  languageCode: string;
  localizedName: string;
  localizedDescription: string;
  originalLanguageCode?: string;
}

export function LocalizationsPanel({
  listId,
  listName,
  listDescription,
  listLanguageCode,
  localizations,
  onChange,
}: LocalizationsPanelProps) {
  const [mode, setMode] = useState<PanelMode>('view');
  const [editingLocalization, setEditingLocalization] = useState<EditingLocalization | null>(null);
  const [initialized, setInitialized] = useState(false);

  const { state: initialDataState } = useInitialData();
  const languages = initialDataState.data?.languages ?? [];

  // Load existing localizations from DB if listId provided
  // Note: useLocalizations already has enabled: !!listId built in
  const { data: dbLocalizations, isLoading } = useLocalizations(listId ?? '');

  const generateLocalizations = useGenerateDraftLocalizations();

  // Initialize from DB data once loaded
  useEffect(() => {
    if (listId && dbLocalizations && !initialized) {
      onChange(
        dbLocalizations.map((loc) => ({
          languageCode: loc.languageCode,
          localizedName: loc.localizedName,
          localizedDescription: loc.localizedDescription,
        }))
      );
      setInitialized(true);
    }
  }, [listId, dbLocalizations, initialized, onChange]);

  // Reset initialized flag if listId changes
  useEffect(() => {
    if (!listId) {
      setInitialized(true); // No DB data to load for create mode
    }
  }, [listId]);

  // Filter languages to exclude the list's own language and zxx
  const availableLanguages = languages.filter(
    (lang) => lang.code !== listLanguageCode && lang.code !== 'zxx' && lang.code !== 'other'
  );

  // Languages that don't have localizations yet
  const unusedLanguages = availableLanguages.filter(
    (lang) => !localizations.some((loc) => loc.languageCode === lang.code)
  );

  const getLanguageName = (code: string) => {
    const lang = languages.find((l) => l.code === code);
    return lang?.name ?? code;
  };

  const handleAddNew = () => {
    if (unusedLanguages.length === 0) {
      toast.error('All available languages have localizations');
      return;
    }
    setEditingLocalization({
      languageCode: unusedLanguages[0]?.code ?? '',
      localizedName: '',
      localizedDescription: '',
    });
    setMode('add');
  };

  const handleEdit = (loc: WordListLocalizationCreate) => {
    setEditingLocalization({
      languageCode: loc.languageCode,
      localizedName: loc.localizedName,
      localizedDescription: loc.localizedDescription ?? '',
      originalLanguageCode: loc.languageCode,
    });
    setMode('edit');
  };

  const handleSave = () => {
    if (!editingLocalization) return;

    if (!editingLocalization.localizedName.trim()) {
      toast.error('Localized name is required');
      return;
    }

    const newLoc: WordListLocalizationCreate = {
      languageCode: editingLocalization.languageCode,
      localizedName: editingLocalization.localizedName.trim(),
      localizedDescription: editingLocalization.localizedDescription.trim() || null,
    };

    if (mode === 'add') {
      onChange([...localizations, newLoc]);
    } else if (mode === 'edit' && editingLocalization.originalLanguageCode) {
      onChange(
        localizations.map((loc) =>
          loc.languageCode === editingLocalization.originalLanguageCode ? newLoc : loc
        )
      );
    }

    setMode('view');
    setEditingLocalization(null);
  };

  const handleDelete = (languageCode: string) => {
    if (!confirm(`Delete localization for ${getLanguageName(languageCode)}?`)) return;
    onChange(localizations.filter((loc) => loc.languageCode !== languageCode));
  };

  const handleCancel = () => {
    setMode('view');
    setEditingLocalization(null);
  };

  const handleGenerate = () => {
    if (!listName.trim()) {
      toast.error('Please enter a list name first');
      return;
    }

    if (unusedLanguages.length === 0) {
      toast.error('All available languages have localizations');
      return;
    }

    generateLocalizations.mutate(
      {
        name: listName,
        description: listDescription ?? undefined,
        sourceLanguage: listLanguageCode,
        targetLanguages: unusedLanguages.map((lang) => lang.code),
      },
      {
        onSuccess: (generated) => {
          const newLocalizations: WordListLocalizationCreate[] = generated.map((loc) => ({
            languageCode: loc.languageCode,
            localizedName: loc.localizedName,
            localizedDescription: loc.localizedDescription,
          }));
          onChange([...localizations, ...newLocalizations]);
          toast.success(`Generated ${generated.length} localization(s)`);
        },
        onError: (error) => {
          toast.error(`Failed to generate: ${(error as Error).message}`);
        },
      }
    );
  };

  // Show loading only for edit mode while fetching DB data
  if (listId && isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Localizations</h3>
            <p className="text-sm text-gray-500 mt-1">
              Translate list name and description for display in other languages
            </p>
          </div>
          {mode === 'view' && unusedLanguages.length > 0 && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generateLocalizations.isPending || !listName.trim()}
                className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {generateLocalizations.isPending ? 'Generating...' : 'Generate with AI'}
              </button>
              <button
                type="button"
                onClick={handleAddNew}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Manually
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* View Mode */}
        {mode === 'view' && (
          <>
            {localizations.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Language
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Localized Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {localizations.map((loc) => (
                      <tr key={loc.languageCode}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {getLanguageName(loc.languageCode)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{loc.localizedName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {loc.localizedDescription || (
                            <span className="text-gray-400 italic">No description</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleEdit(loc)}
                            className="text-blue-600 hover:text-blue-800 text-sm mr-3"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(loc.languageCode)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <p className="text-sm">No localizations added yet.</p>
                <p className="text-xs mt-1">
                  Add translations so users see the list in their native language.
                </p>
              </div>
            )}

            {/* Source Info */}
            {listName && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">
                  Original ({getLanguageName(listLanguageCode)})
                </div>
                <div className="text-sm font-medium text-gray-900">{listName}</div>
                {listDescription && (
                  <div className="text-sm text-gray-600 mt-1">{listDescription}</div>
                )}
              </div>
            )}
          </>
        )}

        {/* Add/Edit Mode */}
        {(mode === 'add' || mode === 'edit') && editingLocalization && (
          <div className="space-y-4">
            {mode === 'add' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <select
                  value={editingLocalization.languageCode}
                  onChange={(e) =>
                    setEditingLocalization((prev) =>
                      prev ? { ...prev, languageCode: e.target.value } : null
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {unusedLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {mode === 'edit' && (
              <div className="text-sm text-gray-600">
                Editing: <strong>{getLanguageName(editingLocalization.languageCode)}</strong>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localized Name *
              </label>
              <input
                type="text"
                value={editingLocalization.localizedName}
                onChange={(e) =>
                  setEditingLocalization((prev) =>
                    prev ? { ...prev, localizedName: e.target.value } : null
                  )
                }
                placeholder={listName || 'Enter localized name'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localized Description
              </label>
              <textarea
                value={editingLocalization.localizedDescription}
                onChange={(e) =>
                  setEditingLocalization((prev) =>
                    prev ? { ...prev, localizedDescription: e.target.value } : null
                  )
                }
                placeholder={listDescription || 'Optional description'}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {mode === 'add' ? 'Add' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
